// services/api.service.ts
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { Service } from "../components/FormComponents/CustomerForm.types";

/** ---- Axios instance with CSRF support ---- */
const api = axios.create({
  baseURL: "api", // works behind your reverse proxy
  withCredentials: true, // needed for CSRF secret cookie
  headers: { "Content-Type": "application/json; charset=utf-8" },
});

export type CustomerCheckBillRequest =
  | {
      customerCode: string;
      identificationType?: "invoice";
      invoiceNumber: string;
    }
  | {
      customerCode: string;
      identificationType: "id";
      personalIdNumber: string;
    }
  | {
      customerCode: string;
      identificationType: "phone";
      mobilePhoneNumber: string;
    };

export type CustomerCheckBillResponse = void | 3;

let csrfToken: string | null = null;
let fetchingTokenPromise: Promise<string> | null = null;

async function fetchCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken;
  if (!fetchingTokenPromise) {
    fetchingTokenPromise = api
      .get("csrf-token")
      .then((res) => {
        csrfToken = res.data?.csrfToken;
        return csrfToken!;
      })
      .finally(() => {
        fetchingTokenPromise = null;
      });
  }
  return fetchingTokenPromise;
}

function setHeaderSafe(
  headers: InternalAxiosRequestConfig["headers"],
  key: string,
  value: string,
) {
  // Axios v1 uses AxiosHeaders with .set()
  let anyHeaders = headers as any;
  if (anyHeaders && typeof anyHeaders.set === "function") {
    anyHeaders.set(key, value);
  } else {
    // Fallback for loose typing / older shapes
    anyHeaders || (anyHeaders = {});
    (anyHeaders as Record<string, any>)[key] = value;
    return anyHeaders;
  }
  return headers;
}

// Attach CSRF token for unsafe methods
api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const method = (config.method || "get").toLowerCase();
  const needsToken = ["post", "put", "patch", "delete"].includes(method);

  if (needsToken) {
    const token = await fetchCsrfToken();
    config.headers = setHeaderSafe(config.headers, "X-CSRF-Token", token);
  }
  return config;
});

// Retry once on EBADCSRFTOKEN (403)
api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError<any>) => {
    const status = error.response?.status;
    const code = (error.response?.data as any)?.code;
    const cfg: any = error.config;

    if (status === 403 && code === "EBADCSRFTOKEN" && !cfg?._retry) {
      cfg._retry = true;
      csrfToken = null; // force refresh
      await fetchCsrfToken();
      cfg.headers = setHeaderSafe(cfg.headers, "X-CSRF-Token", csrfToken!);
      return api(cfg);
    }

    return Promise.reject(error);
  },
);

/** ---- Public API ---- */
export const submitFormData = async (
  data: any,
  service?: Service,
): Promise<"success" | "error"> => {
  try {
    // If this is FormData (file upload), append service fields and send multipart
    if (typeof FormData !== "undefined" && data instanceof FormData) {
      if (service?.value) {
        data.set("service_value", service.value);
        data.set("contact_type", service.value);
      }
      // Remove/override JSON header to allow multipart
      const response = await api.post("customer-contact", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.status === 201) return "success";
      const error: any = new Error("Submission failed");
      error.response = response;
      throw error;
    }

    // Otherwise, JSON payload
    const response = await api.post("customer-contact", {
      ...data,
      service_value: service?.value || null,
      contact_type: service?.value || null,
    });

    if (response.status === 201) return "success";

    const error: any = new Error("Submission failed");
    error.response = response;
    throw error;
  } catch (err: any) {
    // Allow UI to handle duplicate modal
    if (err.response && err.response.status === 409) {
      throw err;
    }
    return "error";
  }
};

export async function customerCheckBill(
  payload: CustomerCheckBillRequest,
): Promise<CustomerCheckBillResponse> {
  const response = await api.post<CustomerCheckBillResponse>(
    "customer-check-bill",
    payload,
  );

  return response.status === 200 ? response.data : undefined;
}

export default api;
